import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const safeGet = (url, fallback) => axios.get(url).catch(() => ({ data: fallback }));

const mergeMeetings = (events, outlookEvents) => {
  const existing = new Set(events.map((m) => m.source_id || m.id));
  const extra = outlookEvents.filter((e) => !existing.has(e.source_id) && !existing.has(e.id));
  return [...events, ...extra].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
};

export const useCalendarData = (userId) => {
  const [meetings, setMeetings] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [connectingOutlook, setConnectingOutlook] = useState(false);

  const fetchIntegrationStatus = useCallback(async () => {
    try {
      setIntegrationStatus((await axios.get(`${API}/calendar/status`)).data);
    } catch (error) {
      console.error("Error fetching integration status:", error);
    }
  }, []);

  const fetchOutlookStatus = useCallback(async () => {
    if (!userId) return;
    try {
      setOutlookStatus((await axios.get(`${API}/calendar/outlook/status/${userId}`)).data);
    } catch (error) {
      console.error("Error fetching Outlook status:", error);
    }
  }, [userId]);

  const fetchCalendarData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [eventsRes, recordingsRes, outlookRes] = await Promise.all([
        safeGet(`${API}/calendar/events/${userId}?hours=168`, { events: [] }),
        safeGet(`${API}/zoom/recordings`, { recordings: [] }),
        safeGet(`${API}/calendar/outlook/events/${userId}?days=7`, { events: [] }),
      ]);
      setMeetings(mergeMeetings(eventsRes.data.events || [], outlookRes.data.events || []));
      setRecordings(recordingsRes.data.recordings || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      setMeetings([]);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const refreshAll = useCallback(() => { fetchOutlookStatus(); fetchCalendarData(); }, [fetchOutlookStatus, fetchCalendarData]);

  useEffect(() => {
    fetchCalendarData();
    fetchIntegrationStatus();
    fetchOutlookStatus();

    const handleOAuthMessage = (event) => {
      if (event.data?.type !== "outlook_auth") return;
      if (event.data.status === "success") {
        toast.success("Outlook Calendar connected successfully!");
        refreshAll();
      } else {
        toast.error("Failed to connect Outlook Calendar. Please try again.");
      }
      setConnectingOutlook(false);
    };
    window.addEventListener("message", handleOAuthMessage);

    const params = new URLSearchParams(window.location.search);
    if (params.get("outlook_connected") === "true") {
      toast.success("Outlook Calendar connected successfully!");
      refreshAll();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get("outlook_error")) {
      toast.error("Failed to connect Outlook Calendar. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [fetchCalendarData, fetchIntegrationStatus, fetchOutlookStatus, refreshAll]);

  const fetchBriefs = useCallback(async () => {
    if (!userId) return;
    setBriefsLoading(true);
    try {
      setBriefs((await axios.get(`${API}/calendar/briefs/${userId}?days=7`)).data.briefs || []);
    } catch (error) {
      console.error("Error fetching briefs:", error);
      setBriefs([]);
    } finally {
      setBriefsLoading(false);
    }
  }, [userId]);

  const connectOutlook = async () => {
    setConnectingOutlook(true);
    try {
      const { data } = await axios.get(`${API}/calendar/outlook/connect/${userId}`);
      if (!data.authorization_url) return;
      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(data.authorization_url, "OutlookAuth", `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          setConnectingOutlook(false);
          refreshAll();
        }
      }, 500);
    } catch (error) {
      console.error("Error connecting Outlook:", error);
      toast.error("Failed to initiate Outlook connection");
      setConnectingOutlook(false);
    }
  };

  const disconnectOutlook = async () => {
    try {
      await axios.delete(`${API}/calendar/outlook/disconnect/${userId}`);
      toast.success("Outlook Calendar disconnected");
      setOutlookStatus({ connected: false });
      fetchCalendarData();
    } catch (error) {
      console.error("Error disconnecting Outlook:", error);
      toast.error("Failed to disconnect Outlook");
    }
  };

  const createMeeting = async () => {
    setCreatingMeeting(true);
    try {
      const { data } = await axios.post(`${API}/zoom/meetings`, { topic: "Quick Sales Call", duration: 30, description: "Created from ISM dashboard" });
      if (data.success) {
        toast.success("Zoom meeting created!");
        navigator.clipboard.writeText(data.meeting.join_url);
        toast.info("Meeting link copied to clipboard");
        fetchCalendarData();
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error("Failed to create meeting");
    } finally {
      setCreatingMeeting(false);
    }
  };

  const addAttendee = async (meetingId, email) => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return false;
    }
    try {
      const existing = meetings.find((m) => m.id === meetingId)?.attendees || [];
      await axios.post(`${API}/calendar/meeting/${meetingId}/attendees?user_id=${userId}`, [...existing, { email }]);
      toast.success("Attendee added! Refresh to generate brief.");
      fetchCalendarData();
      setBriefs([]);
      return true;
    } catch (error) {
      console.error("Error adding attendee:", error);
      toast.error("Failed to add attendee");
      return false;
    }
  };

  return {
    meetings, recordings, briefs, loading, briefsLoading, integrationStatus, outlookStatus, creatingMeeting, connectingOutlook,
    fetchCalendarData, fetchBriefs, refreshAll, connectOutlook, disconnectOutlook, createMeeting, addAttendee,
  };
};
